import { defineExperiment } from '@learnforge/demo-render/harness';
import type { ExperimentSpec, PanelRow, ActionBarSpec } from '@learnforge/demo-render/harness';
import type {
  FigureElement,
  FigureHandle,
  MarksHandle,
  ReadoutHandle,
  ReadoutRow,
  GridHandle,
} from '@learnforge/demo-render';
import type { GeometryContext } from '../types';
import type { GeometryKey } from '../locales/keys';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import {
  midpoint,
  slope,
  sectionPoint,
  shoelaceAreaExact,
  latticeDistance,
  perpBisector,
  arePerpendicular,
} from '@learnforge/demo-math/geometry';
import { formatRadical, Fraction } from '@learnforge/demo-math/numeric';
import { COORD_PRESETS } from '../data/coord-presets';
import type { CoordMode } from '../data/coord-presets';

// ============================================================================
// Glyphs
// ============================================================================

/** Displayed when slope is undefined (vertical segment). */
export const SLOPE_UNDEFINED_GLYPH = 'undefined';

/** Displayed when points are coincident (degenerate, design §11). */
export const COINCIDENT_GLYPH = '—';

// ============================================================================
// Pure Helpers — exported and tested
// ============================================================================

export interface SegmentModel {
  distanceText: string;
  midpointText: string;
  slopeText: string;
}

/**
 * Formats a Fraction coordinate for display in a midpoint tuple.
 * Returns e.g. "5/2" or "3" (integer).
 */
function formatFrac(f: Fraction): string {
  return f.toString();
}

/**
 * Formats an exact distance from latticeDistance result.
 * Returns e.g. "5", "√50 = 5√2", "√2"
 */
function formatDistance(a: Vec2, b: Vec2): string {
  const { n, simplified } = latticeDistance(a, b);
  const [coeff, radicand] = simplified;
  // Perfect integer case (radicand=1 means sqrt(n) is integer)
  if (radicand === 1) {
    return String(coeff);
  }
  // Show both original radicand and simplified form when they differ
  const simplified_str = formatRadical(coeff, radicand);
  if (n !== radicand) {
    // e.g. √50 = 5√2
    return `√${n} = ${simplified_str}`;
  }
  // Already square-free, just show √n (coeff=1 here)
  return `√${n}`;
}

/**
 * Build the segment display model (pure, DOM-free).
 */
export function buildSegmentModel(a: Vec2, b: Vec2): SegmentModel {
  // Coincident case
  if (a.x === b.x && a.y === b.y) {
    return {
      distanceText: '0',
      midpointText: `(${a.x}, ${a.y})`,
      slopeText: COINCIDENT_GLYPH,
    };
  }

  const distanceText = formatDistance(a, b);

  const [mx, my] = midpoint(a, b);
  const midpointText = `(${formatFrac(mx)}, ${formatFrac(my)})`;

  const s = slope(a, b);
  const slopeText = s === 'vertical' ? SLOPE_UNDEFINED_GLYPH : formatFrac(s);

  return { distanceText, midpointText, slopeText };
}

// ============================================================================

export interface ParallelPerpendicularModel {
  slope1Text: string;
  slope2Text: string;
  /** Product text (m₁·m₂) — only meaningful when both slopes are Fraction. */
  productText: string;
  isParallel: boolean;
  isPerpendicular: boolean;
}

/**
 * Build the parallel/perpendicular display model (pure, DOM-free).
 */
export function buildParallelPerpendicularModel(
  a: Vec2,
  b: Vec2,
  c: Vec2,
  d: Vec2,
): ParallelPerpendicularModel {
  const s1 = slope(a, b);
  const s2 = slope(c, d);

  const slope1Text = s1 === 'vertical' ? SLOPE_UNDEFINED_GLYPH : formatFrac(s1);
  const slope2Text = s2 === 'vertical' ? SLOPE_UNDEFINED_GLYPH : formatFrac(s2);

  // Parallel: both vertical, or both Fraction-equal
  let isParallel: boolean;
  if (s1 === 'vertical' && s2 === 'vertical') {
    isParallel = true;
  } else if (s1 === 'vertical' || s2 === 'vertical') {
    isParallel = false;
  } else {
    isParallel = s1.equals(s2);
  }

  const isPerpendicular = arePerpendicular(s1, s2);

  // Product text
  let productText = '';
  if (s1 !== 'vertical' && s2 !== 'vertical') {
    productText = formatFrac(s1.mul(s2));
  }

  return { slope1Text, slope2Text, productText, isParallel, isPerpendicular };
}

// ============================================================================

export interface SectionModel {
  pText: string;
  ratioText: string;
  pCoords: [Fraction, Fraction];
}

/**
 * Build section-point display model: P divides AB in ratio m:n from A.
 */
export function buildSectionModel(a: Vec2, b: Vec2, m: number, n: number): SectionModel {
  const [px, py] = sectionPoint(a, b, m, n);
  const pText = `(${formatFrac(px)}, ${formatFrac(py)})`;
  const ratioText = `${m} : ${n}`;
  return { pText, ratioText, pCoords: [px, py] };
}

// ============================================================================

export interface ShoelaceRunningRow {
  edge: string; // e.g. "P₀P₁"
  term: string; // e.g. "+12"
  runningSum: string; // absolute running |½·Σ|
}

export interface ShoelaceModel {
  areaText: string;
  runningRows: ShoelaceRunningRow[];
}

/**
 * Build the shoelace display model: running cross-terms and exact area.
 */
export function buildShoelaceModel(points: Vec2[]): ShoelaceModel {
  if (points.length < 3) {
    return { areaText: '0', runningRows: [] };
  }

  const n = points.length;
  const runningRows: ShoelaceRunningRow[] = [];
  let runSum = Fraction.ZERO;

  for (let i = 0; i < n; i++) {
    const curr = points[i]!;
    const next = points[(i + 1) % n]!;

    const currX = new Fraction(curr.x, 1);
    const currY = new Fraction(curr.y, 1);
    const nextX = new Fraction(next.x, 1);
    const nextY = new Fraction(next.y, 1);

    const term = currX.mul(nextY).sub(nextX.mul(currY));
    runSum = runSum.add(term);

    // Running area = |runSum / 2|
    const halfAbs = runSum.mul(new Fraction(1, 2)).abs;

    runningRows.push({
      edge: `P${i}P${(i + 1) % n}`,
      term: (term.num >= 0 ? '+' : '') + formatFrac(term),
      runningSum: formatFrac(halfAbs),
    });
  }

  // Final area is |shoelaceAreaExact| = absolute value of signed area
  const signed = shoelaceAreaExact(points);
  const areaText = formatFrac(signed.abs);

  return { areaText, runningRows };
}

// ============================================================================

export interface LocusModel {
  paText: string;
  pbText: string;
  isOnBisector: boolean;
  bisector: { point: { x: number; y: number }; dir: { x: number; y: number } } | null;
}

/**
 * Build the locus display model: PA, PB distances and bisector test.
 */
export function buildLocusModel(a: Vec2, b: Vec2, p: Vec2): LocusModel {
  // Use latticeDistance for exact integer squared distances, then format radicals
  const { n: nPA, simplified: sPA } = latticeDistance(a, p);
  const { n: nPB, simplified: sPB } = latticeDistance(b, p);

  function fmtDist(n: number, s: [number, number]): string {
    const [coeff, radicand] = s;
    if (radicand === 1) return String(coeff);
    const simplified_str = formatRadical(coeff, radicand);
    if (n !== radicand) return `√${n} = ${simplified_str}`;
    return `√${n}`;
  }

  const paText = fmtDist(nPA, sPA);
  const pbText = fmtDist(nPB, sPB);

  // P is on the bisector iff PA² === PB² (exact integer comparison)
  const isOnBisector = nPA === nPB;

  // Only compute bisector if A !== B
  const bisector = a.x === b.x && a.y === b.y ? null : perpBisector(a, b);

  return { paText, pbText, isOnBisector, bisector };
}

// ============================================================================
// Constants
// ============================================================================

const STAGE_W = 800;
const STAGE_H = 460;
const GRID_UNIT = 40;

// Grid world bounds with GRID_UNIT=40, stage 800×460:
// World x: 0..20, World y: 0..11.5
// Draggable range: x [1..19], y [1..11]
const WORLD_MAX_X = Math.floor(STAGE_W / GRID_UNIT) - 1; // 19
const WORLD_MAX_Y = Math.floor(STAGE_H / GRID_UNIT) - 1; // 11
const WORLD_MIN = 1;

const FOUNDATION_MODES: readonly CoordMode[] = ['segment', 'parallel-perpendicular'];

/** Snap a screen-coordinate point to the nearest integer lattice point. */
function snapToLattice(grid: GridHandle, screenPt: Vec2): Vec2 {
  const world = grid.screenToWorld(screenPt);
  const snappedWorld = {
    x: Math.max(WORLD_MIN, Math.min(WORLD_MAX_X, Math.round(world.x))),
    y: Math.max(WORLD_MIN, Math.min(WORLD_MAX_Y, Math.round(world.y))),
  };
  return grid.worldToScreen(snappedWorld);
}

/** Convert screen position to a clamped integer world point. */
function screenToLatticeWorld(grid: GridHandle, screenPt: Vec2): Vec2 {
  const world = grid.screenToWorld(screenPt);
  return {
    x: Math.max(WORLD_MIN, Math.min(WORLD_MAX_X, Math.round(world.x))),
    y: Math.max(WORLD_MIN, Math.min(WORLD_MAX_Y, Math.round(world.y))),
  };
}

/** Convert integer world coords to screen coords. */
function latticeToScreen(grid: GridHandle, p: Vec2): Vec2 {
  return grid.worldToScreen(p);
}

// ============================================================================
// State shape (all points in world / integer-lattice coordinates)
// ============================================================================

interface CoordState {
  mode: CoordMode;
  showRiseRun: boolean;
  showAngles: boolean;

  // segment
  ptA: Vec2;
  ptB: Vec2;

  // parallel-perpendicular
  ppA: Vec2;
  ppB: Vec2;
  ptC: Vec2;
  ptD: Vec2;

  // section
  secA: Vec2;
  secB: Vec2;
  sectionM: number;
  sectionN: number;

  // shoelace
  shoelacePts: Vec2[];

  // locus
  locusA: Vec2;
  locusB: Vec2;
  locusP: Vec2;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): ExperimentSpec<CoordState, GeometryKey, GeometryContext> {
  // Closure-stashed handles created in setup(). The grid is created ONCE (its
  // params are constant: unit=40, faint=false), in z-order BEFORE the figure so
  // it renders behind it — mirroring the bespoke mount (svg → grid → figure →
  // marks → readout). `render` reads `grid.worldToScreen` to position points.
  let grid: GridHandle;
  let figure: FigureHandle;
  let marks: MarksHandle;
  let readout: ReadoutHandle;

  /** The canonical default model — segment mode, A(3,8) B(8,3), overlays on. */
  function defaultState(): CoordState {
    return {
      mode: 'segment',
      showRiseRun: true,
      showAngles: true,
      ptA: { x: 3, y: 8 },
      ptB: { x: 8, y: 3 },
      ppA: { x: 3, y: 8 },
      ppB: { x: 8, y: 3 },
      ptC: { x: 2, y: 3 },
      ptD: { x: 6, y: 5 },
      secA: { x: 2, y: 9 },
      secB: { x: 17, y: 4 },
      sectionM: 1,
      sectionN: 1,
      shoelacePts: [
        { x: 5, y: 2 },
        { x: 10, y: 4 },
        { x: 9, y: 9 },
        { x: 3, y: 9 },
      ],
      locusA: { x: 5, y: 6 },
      locusB: { x: 15, y: 6 },
      locusP: { x: 10, y: 3 },
    };
  }

  /**
   * Apply a preset entry to the model. PURE. Respects advancedOnly vs advanced.
   * Mirrors the bespoke `?preset=` reader + action-bar `onPreset`, which set the
   * mode then patched only the fields the preset declared.
   */
  function applyPresetLocal(state: CoordState, presetId: string, advanced: boolean): CoordState {
    const preset = COORD_PRESETS.find((p) => p.id === presetId);
    if (!preset || (preset.advancedOnly && !advanced)) return state;

    const next: CoordState = { ...state, mode: preset.mode };

    if (preset.ptA) {
      const [x, y] = preset.ptA;
      if (preset.mode === 'segment') {
        next.ptA = { x, y };
      } else if (preset.mode === 'parallel-perpendicular') {
        next.ppA = { x, y };
      } else if (preset.mode === 'section') {
        next.secA = { x, y };
      }
    }
    if (preset.ptB) {
      const [x, y] = preset.ptB;
      if (preset.mode === 'segment') {
        next.ptB = { x, y };
      } else if (preset.mode === 'parallel-perpendicular') {
        next.ppB = { x, y };
      } else if (preset.mode === 'section') {
        next.secB = { x, y };
      }
    }
    if (preset.ptC) {
      const [x, y] = preset.ptC;
      next.ptC = { x, y };
    }
    if (preset.ptD) {
      const [x, y] = preset.ptD;
      next.ptD = { x, y };
    }
    if (preset.sectionM !== undefined) next.sectionM = preset.sectionM;
    if (preset.sectionN !== undefined) next.sectionN = preset.sectionN;
    if (preset.polygonVertices) {
      next.shoelacePts = preset.polygonVertices.map(([x, y]) => ({ x, y }));
    }

    return next;
  }

  /**
   * The mode used for display + the dropdown value. When advanced is off and the
   * stored mode is advanced-only, it falls back to 'segment' — the bespoke
   * rebuildModeSelect / advanced-off handler reset currentMode to 'segment' the
   * same way. Used read-only in render/panel/actions (never mutates state).
   */
  function effectiveModeFor(state: CoordState, advanced: boolean): CoordMode {
    return !advanced && !FOUNDATION_MODES.includes(state.mode) ? 'segment' : state.mode;
  }

  return {
    labelKey: 'experiment.coordinate-geometry',
    demoId: '2d-geometry',
    advancedKey: 'coordinate-geometry',

    initialState(): CoordState {
      return defaultState();
    },

    applyPreset(state, presetId, ctx): CoordState {
      return applyPresetLocal(state, presetId, ctx.advanced);
    },

    setup(_ctx, controller) {
      // Z-order (back → front) matches the bespoke mount exactly:
      // grid (behind) → figure → marks → readout (separate #readout container).
      grid = controller.createGrid({
        width: STAGE_W,
        height: STAGE_H,
        unit: GRID_UNIT,
        faint: false,
      });
      figure = controller.createFigure();
      marks = controller.createMarks();
      readout = controller.createReadout();
    },

    render(state: CoordState, ctx): void {
      // Advanced-off falls back advanced-only modes to segment for display,
      // matching the bespoke advanced-off handler. PURE: state is untouched.
      const effectiveMode = effectiveModeFor(state, ctx.advanced);
      switch (effectiveMode) {
        case 'segment':
          renderSegmentMode(state);
          break;
        case 'parallel-perpendicular':
          renderParallelPerpendicularMode(state);
          break;
        case 'section':
          renderSectionMode(state);
          break;
        case 'shoelace':
          renderShoelaceMode(state);
          break;
        case 'locus':
          renderLocusMode(state);
          break;
      }

      function renderSegmentMode(state: CoordState): void {
        const { ptA, ptB } = state;
        const sA = latticeToScreen(grid, ptA);
        const sB = latticeToScreen(grid, ptB);
        const constraint = (screenPt: Vec2): Vec2 => snapToLattice(grid, screenPt);

        const model = buildSegmentModel(ptA, ptB);
        const elems: FigureElement[] = [];

        elems.push({
          kind: 'point',
          id: 'seg-A',
          at: sA,
          draggable: { constraint },
          style: { className: 'geo-preimage-pt' },
        });
        elems.push({
          kind: 'point',
          id: 'seg-B',
          at: sB,
          draggable: { constraint },
          style: { className: 'geo-preimage-pt' },
        });
        elems.push({
          kind: 'segment',
          id: 'seg-AB',
          from: 'seg-A',
          to: 'seg-B',
          style: { className: 'geo-tri-side' },
        });
        elems.push({
          kind: 'label',
          id: 'lbl-A',
          anchor: 'seg-A',
          offset: { x: -18, y: -10 },
          text: 'A',
        });
        elems.push({
          kind: 'label',
          id: 'lbl-B',
          anchor: 'seg-B',
          offset: { x: 8, y: -10 },
          text: 'B',
        });

        // Rise/run triangle overlay (dashed)
        if (state.showRiseRun && ptA.x !== ptB.x && ptA.y !== ptB.y) {
          // Corner at (B.x, A.y) in world, screen
          const corner = latticeToScreen(grid, { x: ptB.x, y: ptA.y });
          elems.push({ kind: 'point', id: 'seg-corner', at: corner });
          elems.push({
            kind: 'segment',
            id: 'seg-run',
            from: 'seg-A',
            to: 'seg-corner',
            style: { className: 'geo-intercept-seg', dashed: true },
          });
          elems.push({
            kind: 'segment',
            id: 'seg-rise',
            from: 'seg-corner',
            to: 'seg-B',
            style: { className: 'geo-intercept-seg', dashed: true },
          });
        }

        figure.update(elems);

        // Angle display
        if (state.showAngles) {
          const deg = Math.abs((Math.atan2(-(sB.y - sA.y), sB.x - sA.x) * 180) / Math.PI);
          marks.update([
            {
              kind: 'angle',
              id: 'ang-A',
              vertex: sA,
              from: { x: sA.x + 40, y: sA.y },
              to: sB,
              label: `${Math.round(deg)}°`,
            },
          ]);
        } else {
          marks.update([]);
        }

        const rows: ReadoutRow[] = [
          {
            id: 'dist',
            lhs: '|AB|',
            relation: '=',
            rhs: model.distanceText,
            emphasis: 'invariant',
          },
          { id: 'mid', lhs: 'M', relation: '=', rhs: model.midpointText, emphasis: 'neutral' },
          { id: 'slp', lhs: 'm', relation: '=', rhs: model.slopeText, emphasis: 'neutral' },
        ];
        readout.update(rows);
      }

      function renderParallelPerpendicularMode(state: CoordState): void {
        const { ppA, ppB, ptC, ptD } = state;
        const sA = latticeToScreen(grid, ppA);
        const sB = latticeToScreen(grid, ppB);
        const sC = latticeToScreen(grid, ptC);
        const sD = latticeToScreen(grid, ptD);
        const constraint = (screenPt: Vec2): Vec2 => snapToLattice(grid, screenPt);

        const model = buildParallelPerpendicularModel(ppA, ppB, ptC, ptD);

        const elems: FigureElement[] = [
          {
            kind: 'point',
            id: 'pp-A',
            at: sA,
            draggable: { constraint },
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'pp-B',
            at: sB,
            draggable: { constraint },
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'pp-C',
            at: sC,
            draggable: { constraint },
            style: { className: 'geo-image-pt' },
          },
          {
            kind: 'point',
            id: 'pp-D',
            at: sD,
            draggable: { constraint },
            style: { className: 'geo-image-pt' },
          },
          {
            kind: 'segment',
            id: 'pp-seg1',
            from: 'pp-A',
            to: 'pp-B',
            style: { className: 'geo-tri-side' },
          },
          {
            kind: 'segment',
            id: 'pp-seg2',
            from: 'pp-C',
            to: 'pp-D',
            style: { className: 'geo-intercept-seg' },
          },
          { kind: 'label', id: 'lbl-ppA', anchor: 'pp-A', offset: { x: -18, y: -10 }, text: 'A' },
          { kind: 'label', id: 'lbl-ppB', anchor: 'pp-B', offset: { x: 8, y: -10 }, text: 'B' },
          { kind: 'label', id: 'lbl-ppC', anchor: 'pp-C', offset: { x: -18, y: 12 }, text: 'C' },
          { kind: 'label', id: 'lbl-ppD', anchor: 'pp-D', offset: { x: 8, y: 12 }, text: 'D' },
        ];
        figure.update(elems);

        // Angle display: inclination of first line at its start point
        if (state.showAngles) {
          const deg1 = Math.abs((Math.atan2(-(sB.y - sA.y), sB.x - sA.x) * 180) / Math.PI);
          marks.update([
            {
              kind: 'angle',
              id: 'ang-pp-1',
              vertex: sA,
              from: { x: sA.x + 40, y: sA.y },
              to: sB,
              label: `${Math.round(deg1)}°`,
            },
          ]);
        } else {
          marks.update([]);
        }

        const rows: ReadoutRow[] = [
          { id: 'slp1', lhs: 'm(AB)', relation: '=', rhs: model.slope1Text, emphasis: 'neutral' },
          { id: 'slp2', lhs: 'm(CD)', relation: '=', rhs: model.slope2Text, emphasis: 'neutral' },
        ];

        if (model.isParallel) {
          rows.push({
            id: 'badge',
            lhs: 'AB',
            relation: '∥',
            rhs: 'CD',
            emphasis: 'invariant',
          });
        } else if (model.isPerpendicular) {
          rows.push({
            id: 'badge',
            lhs: 'AB',
            relation: '⊥',
            rhs: 'CD',
            emphasis: 'invariant',
          });
          if (model.productText) {
            rows.push({
              id: 'prod',
              lhs: 'm₁·m₂',
              relation: '=',
              rhs: model.productText,
              emphasis: 'invariant',
            });
          }
        }
        readout.update(rows);
      }

      function renderSectionMode(state: CoordState): void {
        const { secA, secB, sectionM, sectionN } = state;
        const sA = latticeToScreen(grid, secA);
        const sB = latticeToScreen(grid, secB);
        const constraint = (screenPt: Vec2): Vec2 => snapToLattice(grid, screenPt);

        const model = buildSectionModel(secA, secB, sectionM, sectionN);
        // Compute screen position of P
        const pWorld = {
          x: model.pCoords[0].toNumber(),
          y: model.pCoords[1].toNumber(),
        };
        const sP = latticeToScreen(grid, pWorld);

        const elems: FigureElement[] = [
          {
            kind: 'point',
            id: 'sec-A',
            at: sA,
            draggable: { constraint },
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'sec-B',
            at: sB,
            draggable: { constraint },
            style: { className: 'geo-preimage-pt' },
          },
          { kind: 'point', id: 'sec-P', at: sP, style: { className: 'geo-intercept-pt' } },
          {
            kind: 'segment',
            id: 'sec-AB',
            from: 'sec-A',
            to: 'sec-B',
            style: { className: 'geo-tri-side' },
          },
          { kind: 'label', id: 'lbl-secA', anchor: 'sec-A', offset: { x: -18, y: -10 }, text: 'A' },
          { kind: 'label', id: 'lbl-secB', anchor: 'sec-B', offset: { x: 8, y: -10 }, text: 'B' },
          { kind: 'label', id: 'lbl-secP', anchor: 'sec-P', offset: { x: 8, y: -10 }, text: 'P' },
        ];
        figure.update(elems);

        // Angle display
        if (state.showAngles) {
          const deg = Math.abs((Math.atan2(-(sB.y - sA.y), sB.x - sA.x) * 180) / Math.PI);
          marks.update([
            {
              kind: 'angle',
              id: 'ang-sec',
              vertex: sA,
              from: { x: sA.x + 40, y: sA.y },
              to: sB,
              label: `${Math.round(deg)}°`,
            },
          ]);
        } else {
          marks.update([]);
        }

        const rows: ReadoutRow[] = [
          { id: 'ratio', lhs: 'AP : PB', relation: '=', rhs: model.ratioText, emphasis: 'neutral' },
          { id: 'coords', lhs: 'P', relation: '=', rhs: model.pText, emphasis: 'invariant' },
        ];
        readout.update(rows);
      }

      function renderShoelaceMode(state: CoordState): void {
        const constraint = (screenPt: Vec2): Vec2 => snapToLattice(grid, screenPt);
        const pts = state.shoelacePts;
        const n = pts.length;

        const model = buildShoelaceModel(pts);
        const elems: FigureElement[] = [];

        // Polygon points (draggable)
        for (let i = 0; i < n; i++) {
          const sp = latticeToScreen(grid, pts[i]!);
          elems.push({
            kind: 'point',
            id: `shoe-${i}`,
            at: sp,
            draggable: { constraint },
            style: { className: 'geo-preimage-pt' },
          });
        }

        // Polygon outline
        const vIds = pts.map((_, i) => `shoe-${i}`);
        elems.push({
          kind: 'polygon',
          id: 'shoe-poly',
          vertices: vIds,
          style: { className: 'geo-preimage-poly' },
        });

        // Labels
        for (let i = 0; i < n; i++) {
          elems.push({
            kind: 'label',
            id: `lbl-shoe-${i}`,
            anchor: `shoe-${i}`,
            offset: { x: 8, y: -10 },
            text: `P${i}`,
          });
        }
        figure.update(elems);

        marks.update([]);

        const rows: ReadoutRow[] = model.runningRows.map((r, i) => ({
          id: `edge-${i}`,
          lhs: r.edge,
          relation: '=' as const,
          rhs: `${r.term} → ${r.runningSum}`,
          emphasis:
            i === model.runningRows.length - 1 ? ('invariant' as const) : ('neutral' as const),
        }));
        rows.push({
          id: 'area',
          lhs: 'Area',
          relation: '=',
          rhs: model.areaText,
          emphasis: 'invariant',
        });
        readout.update(rows);
      }

      function renderLocusMode(state: CoordState): void {
        const { locusA, locusB, locusP } = state;
        const constraint = (screenPt: Vec2): Vec2 => snapToLattice(grid, screenPt);
        const sA = latticeToScreen(grid, locusA);
        const sB = latticeToScreen(grid, locusB);
        const sP = latticeToScreen(grid, locusP);

        const model = buildLocusModel(locusA, locusB, locusP);
        const elems: FigureElement[] = [
          {
            kind: 'point',
            id: 'loc-A',
            at: sA,
            draggable: { constraint },
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'loc-B',
            at: sB,
            draggable: { constraint },
            style: { className: 'geo-preimage-pt' },
          },
          {
            kind: 'point',
            id: 'loc-P',
            at: sP,
            draggable: { constraint },
            style: { className: 'geo-intercept-pt' },
          },
          {
            kind: 'segment',
            id: 'loc-AB',
            from: 'loc-A',
            to: 'loc-B',
            style: { className: 'geo-tri-base' },
          },
          {
            kind: 'segment',
            id: 'loc-PA',
            from: 'loc-P',
            to: 'loc-A',
            style: { className: 'geo-intercept-seg', dashed: true },
          },
          {
            kind: 'segment',
            id: 'loc-PB',
            from: 'loc-P',
            to: 'loc-B',
            style: { className: 'geo-intercept-seg', dashed: true },
          },
          { kind: 'label', id: 'lbl-locA', anchor: 'loc-A', offset: { x: -18, y: -10 }, text: 'A' },
          { kind: 'label', id: 'lbl-locB', anchor: 'loc-B', offset: { x: 8, y: -10 }, text: 'B' },
          { kind: 'label', id: 'lbl-locP', anchor: 'loc-P', offset: { x: 8, y: -10 }, text: 'P' },
        ];
        figure.update(elems);

        marks.update([]);

        const paRel: ReadoutRow['relation'] = model.isOnBisector ? '=' : '≈';
        const rows: ReadoutRow[] = [
          { id: 'pa', lhs: 'PA', relation: '=', rhs: model.paText, emphasis: 'neutral' },
          { id: 'pb', lhs: 'PB', relation: '=', rhs: model.pbText, emphasis: 'neutral' },
        ];
        if (model.isOnBisector) {
          rows.push({
            id: 'on-bisector',
            lhs: 'PA',
            relation: paRel,
            rhs: 'PB',
            emphasis: 'invariant',
          });
        }
        readout.update(rows);
      }
    },

    panel(state: CoordState, ctx): PanelRow<GeometryKey, CoordState>[] {
      const effectiveMode = effectiveModeFor(state, ctx.advanced);

      const modeOptions: { value: string; labelKey: GeometryKey }[] = [
        { value: 'segment', labelKey: 'coord.mode.segment' },
        { value: 'parallel-perpendicular', labelKey: 'coord.mode.parallel-perpendicular' },
      ];
      if (ctx.advanced) {
        modeOptions.push(
          { value: 'section', labelKey: 'coord.mode.section' },
          { value: 'shoelace', labelKey: 'coord.mode.shoelace' },
          { value: 'locus', labelKey: 'coord.mode.locus' },
        );
      }

      const rows: PanelRow<GeometryKey, CoordState>[] = [
        {
          kind: 'dropdown',
          id: 'coord-mode',
          labelKey: 'panel.mode',
          options: modeOptions,
          value: effectiveMode,
          onChange: (v) => ({ ...state, mode: v as CoordMode }),
        },
      ];

      // Show rise/run toggle (segment mode only — bespoke hid it elsewhere).
      if (effectiveMode === 'segment') {
        rows.push({
          kind: 'toggle',
          labelKey: 'coord.panel.show-rise-run',
          value: state.showRiseRun,
          onChange: (v) => ({ ...state, showRiseRun: v }),
        });
      }

      // Show angles toggle — meaningful only in modes that draw an angle, so
      // hidden in shoelace/locus (the bespoke hid it the same way).
      if (effectiveMode !== 'shoelace' && effectiveMode !== 'locus') {
        rows.push({
          kind: 'toggle',
          labelKey: 'coord.panel.show-angles',
          value: state.showAngles,
          onChange: (v) => ({ ...state, showAngles: v }),
        });
      }

      // Ratio m / n sliders (section mode only).
      if (effectiveMode === 'section') {
        rows.push(
          {
            kind: 'slider',
            id: 'coord-section-m',
            labelKey: 'coord.panel.ratio-m',
            min: 1,
            max: 9,
            step: 1,
            value: state.sectionM,
            format: (v) => String(v),
            onChange: (v) => ({ ...state, sectionM: v }),
          },
          {
            kind: 'slider',
            id: 'coord-section-n',
            labelKey: 'coord.panel.ratio-n',
            min: 1,
            max: 9,
            step: 1,
            value: state.sectionN,
            format: (v) => String(v),
            onChange: (v) => ({ ...state, sectionN: v }),
          },
        );
      }

      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions: (state: CoordState, ctx): ActionBarSpec<GeometryKey, CoordState> => {
      const effectiveMode = effectiveModeFor(state, ctx.advanced);
      return {
        onReset: () => defaultState(),
        onRandomise: (s) => {
          // Sample random lattice points within the visible grid.
          const rx = (): number => Math.floor(ctx.prng() * (WORLD_MAX_X - WORLD_MIN) + WORLD_MIN);
          const ry = (): number => Math.floor(ctx.prng() * (WORLD_MAX_Y - WORLD_MIN) + WORLD_MIN);
          const next = { ...s };
          if (effectiveMode === 'segment') {
            next.ptA = { x: rx(), y: ry() };
            next.ptB = { x: rx(), y: ry() };
          } else if (effectiveMode === 'parallel-perpendicular') {
            next.ppA = { x: rx(), y: ry() };
            next.ppB = { x: rx(), y: ry() };
            next.ptC = { x: rx(), y: ry() };
            next.ptD = { x: rx(), y: ry() };
          } else if (effectiveMode === 'section') {
            next.secA = { x: rx(), y: ry() };
            next.secB = { x: rx(), y: ry() };
            next.sectionM = Math.max(1, Math.floor(ctx.prng() * 9));
            next.sectionN = Math.max(1, Math.floor(ctx.prng() * 9));
          } else if (effectiveMode === 'shoelace') {
            next.shoelacePts = s.shoelacePts.map(() => ({ x: rx(), y: ry() }));
          } else if (effectiveMode === 'locus') {
            next.locusA = { x: rx(), y: ry() };
            next.locusB = { x: rx(), y: ry() };
            next.locusP = { x: rx(), y: ry() };
          }
          return next;
        },
        presets: COORD_PRESETS.filter(
          (p) => p.mode === effectiveMode && (ctx.advanced || !p.advancedOnly),
        ).map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id, ctx.advanced),
      };
    },

    onDrag(state: CoordState, pointId: string, at: Vec2): CoordState {
      // `at` is the constrained screen position; re-derive the integer world
      // point exactly as the bespoke onDrag did (screenToLatticeWorld).
      const world = screenToLatticeWorld(grid, at);

      // Dispatch on the stored mode (matching the circle-theorems/triangle-lab
      // siblings). Point IDs are unique per mode and `render` only registers
      // draggables for the rendered mode, so a drag can only ever reach the
      // branch whose figure produced it — no ctx.advanced needed here.
      if (state.mode === 'segment') {
        if (pointId === 'seg-A') return { ...state, ptA: world };
        if (pointId === 'seg-B') return { ...state, ptB: world };
      } else if (state.mode === 'parallel-perpendicular') {
        if (pointId === 'pp-A') return { ...state, ppA: world };
        if (pointId === 'pp-B') return { ...state, ppB: world };
        if (pointId === 'pp-C') return { ...state, ptC: world };
        if (pointId === 'pp-D') return { ...state, ptD: world };
      } else if (state.mode === 'section') {
        if (pointId === 'sec-A') return { ...state, secA: world };
        if (pointId === 'sec-B') return { ...state, secB: world };
      } else if (state.mode === 'shoelace') {
        if (pointId.startsWith('shoe-')) {
          const idx = parseInt(pointId.slice(5), 10);
          if (idx >= 0 && idx < state.shoelacePts.length) {
            const shoelacePts = state.shoelacePts.slice();
            shoelacePts[idx] = world;
            return { ...state, shoelacePts };
          }
        }
      } else if (state.mode === 'locus') {
        if (pointId === 'loc-A') return { ...state, locusA: world };
        if (pointId === 'loc-B') return { ...state, locusB: world };
        if (pointId === 'loc-P') return { ...state, locusP: world };
      }
      return state;
    },

    applyShotState(state: CoordState, _shotId: string): CoordState {
      // The coordinate-geometry shot uses NO preset — it is exactly the default
      // pose (segment mode, A(3,8) B(8,3) → |AB| = 5√2 with the rise/run
      // triangle and angle overlay shown). `initialState` already produces that
      // pose, so every shot returns `state` unchanged.
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
